import React, { useState } from 'react';
import { CreditCardIcon, CalendarIcon, DollarSignIcon, FilterIcon, SearchIcon, FileTextIcon, CheckCircleIcon, AlertCircleIcon, DownloadIcon } from 'lucide-react';
interface CustomerTransactionsProps {
  transactions: any[];
}
export const CustomerTransactions: React.FC<CustomerTransactionsProps> = ({
  transactions
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  // Apply filters and sorting
  const filteredTransactions = transactions.filter(transaction => {
    // Apply type filter
    if (typeFilter !== 'all' && transaction.type !== typeFilter) {
      return false;
    }
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return transaction.product.toLowerCase().includes(term) || transaction.status.toLowerCase().includes(term);
    }
    return true;
  }).sort((a, b) => {
    // Sort by date
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2006">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-2007" />
            Completed
          </span>;
      case 'confirmed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2008">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-2009" />
            Confirmed
          </span>;
      case 'processed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2010">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-2011" />
            Processed
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2012">
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>;
    }
  };
  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return <CalendarIcon className="h-5 w-5 text-blue-500" data-id="element-2013" />;
      case 'refund':
        return <DollarSignIcon className="h-5 w-5 text-red-500" data-id="element-2014" />;
      case 'deposit':
        return <DollarSignIcon className="h-5 w-5 text-green-500" data-id="element-2015" />;
      case 'add-on':
        return <PlusIcon className="h-5 w-5 text-purple-500" data-id="element-2016" />;
      default:
        return <CreditCardIcon className="h-5 w-5 text-gray-500" data-id="element-2017" />;
    }
  };
  return <div className="p-6" data-id="element-2018">
      <div className="flex justify-between items-center mb-6" data-id="element-2019">
        <h2 className="text-lg font-medium text-gray-800" data-id="element-2020">
          Transaction History
        </h2>
        <button className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2021">
          <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2022" />
          Export
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6" data-id="element-2023">
        <div className="relative flex-1" data-id="element-2024">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2025">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2026" />
          </div>
          <input type="text" placeholder="Search transactions..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2027" />
        </div>
        <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-id="element-2028">
          <option value="all" data-id="element-2029">All Types</option>
          <option value="booking" data-id="element-2030">Bookings</option>
          <option value="refund" data-id="element-2031">Refunds</option>
          <option value="deposit" data-id="element-2032">Deposits</option>
          <option value="add-on" data-id="element-2033">Add-ons</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={sortOrder} onChange={e => setSortOrder(e.target.value)} data-id="element-2034">
          <option value="newest" data-id="element-2035">Newest First</option>
          <option value="oldest" data-id="element-2036">Oldest First</option>
        </select>
      </div>
      {/* Transactions list */}
      {filteredTransactions.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg" data-id="element-2037">
          <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" data-id="element-2038" />
          <h3 className="text-lg font-medium text-gray-800 mb-2" data-id="element-2039">
            No transactions found
          </h3>
          <p className="text-gray-500" data-id="element-2040">Try adjusting your search or filters</p>
        </div> : <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2041">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2042">
            <thead className="bg-gray-50" data-id="element-2043">
              <tr data-id="element-2044">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2045">
                  Transaction
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2046">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2047">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2048">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2049">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2050">
              {filteredTransactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50" data-id="element-2051">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2052">
                    <div className="flex items-center" data-id="element-2053">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2054">
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div className="ml-4" data-id="element-2055">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2056">
                          {transaction.product}
                        </div>
                        <div className="text-xs text-gray-500 capitalize" data-id="element-2057">
                          {transaction.type}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2058">
                    <div className="text-sm text-gray-900" data-id="element-2059">
                      {formatDate(transaction.date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2060">
                    <div className={`text-sm font-medium ${transaction.type === 'refund' ? 'text-red-600' : 'text-green-600'}`} data-id="element-2061">
                      {transaction.type === 'refund' ? '-' : ''}$
                      {Math.abs(transaction.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2062">
                    {getStatusBadge(transaction.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2063">
                    <button className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2064">
                      <FileTextIcon className="h-4 w-4" data-id="element-2065" />
                    </button>
                    <button className="text-gray-600 hover:text-gray-900" data-id="element-2066">
                      <MoreHorizontalIcon className="h-4 w-4" data-id="element-2067" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
    </div>;
};
const PlusIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-2068">
    <line x1="12" y1="5" x2="12" y2="19" data-id="element-2069"></line>
    <line x1="5" y1="12" x2="19" y2="12" data-id="element-2070"></line>
  </svg>;
const MoreHorizontalIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-2071">
    <circle cx="12" cy="12" r="1" data-id="element-2072"></circle>
    <circle cx="19" cy="12" r="1" data-id="element-2073"></circle>
    <circle cx="5" cy="12" r="1" data-id="element-2074"></circle>
  </svg>;